"use client";

import { Button } from "@shared/components/ui/button";
import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import { ADMIN_HOME_PATH } from "@/lib/admin-env";

export { ADMIN_HOME_PATH };

const AUTH_BASE = "/api/admin/auth";

/** Absolute path on the admin origin (no basePath). */
export const adminPath = (path: string): string => {
  if (!path || path === "/") {
    return "/";
  }
  return path.startsWith("/") ? path : `/${path}`;
};

export const buildLoginUrl = (): string => adminPath("/login");

export const assignAdminPath = (path: string): void => {
  window.location.assign(adminPath(path));
};

export type AdminUser = {
  id: string;
  email: string;
  name: string;
  requiresPasswordChange: boolean;
};

type AuthStatus = "loading" | "authenticated" | "unauthenticated" | "error";

type UserPayload = {
  id: string;
  email: string;
  name: string;
  requires_password_change: boolean;
};

type AuthSuccess = { kind: "authenticated"; user: AdminUser };

type AuthFailure = {
  kind: "invalid_credentials" | "too_many_attempts" | "invalid_code" | "error";
  message: string;
};

export type LoginResult = AuthSuccess | AuthFailure | { kind: "second_factor" };

export type SecondFactorResult = AuthSuccess | AuthFailure;

export type PasswordResult = AuthSuccess | AuthFailure;

type AuthContextValue = {
  user: AdminUser | null;
  loading: boolean;
  status: AuthStatus;
  retry: () => void;
  requestMagicLink: (email: string) => Promise<AuthFailure | { kind: "sent" }>;
  consumeMagicLink: (token: string) => Promise<LoginResult>;
  submitSecondFactor: (code: string) => Promise<SecondFactorResult>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<PasswordResult>;
  signOut: () => Promise<void>;
};

const consumeCache = new Map<string, Promise<LoginResult>>();

const AuthContext = createContext<AuthContextValue | null>(null);

type SessionExpiredListener = () => void;

const sessionExpiredListeners = new Set<SessionExpiredListener>();

/** Drop the signed-in shell when `/api/admin/graphql` returns HTTP 401. */
export const markSessionExpired = (): void => {
  for (const listener of sessionExpiredListeners) {
    try {
      listener();
    } catch {
      // Listeners must not break each other.
    }
  }
};

const subscribeSessionExpired = (listener: SessionExpiredListener): (() => void) => {
  sessionExpiredListeners.add(listener);
  return () => {
    sessionExpiredListeners.delete(listener);
  };
};

const toUser = (payload: UserPayload): AdminUser => ({
  id: payload.id,
  email: payload.email,
  name: payload.name,
  requiresPasswordChange: payload.requires_password_change,
});

const isUserPayload = (value: unknown): value is UserPayload => {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const record = value as Record<string, unknown>;
  return (
    typeof record.id === "string" &&
    typeof record.email === "string" &&
    typeof record.name === "string" &&
    typeof record.requires_password_change === "boolean"
  );
};

const readBody = async (response: Response): Promise<Record<string, unknown>> => {
  try {
    const body: unknown = await response.json();
    if (typeof body === "object" && body !== null) {
      return body as Record<string, unknown>;
    }
  } catch {
    return {};
  }
  return {};
};

const messageFrom = (body: Record<string, unknown>, fallback: string): string => {
  const message = body.message;
  return typeof message === "string" && message.trim().length > 0 ? message : fallback;
};

const failureFromStatus = (status: number, message: string): AuthFailure => {
  if (status === 429) {
    return { kind: "too_many_attempts", message };
  }
  if (status === 401) {
    return { kind: "invalid_credentials", message };
  }
  return { kind: "error", message };
};

const postJson = async (path: string, payload?: Record<string, string>): Promise<Response> => {
  return fetch(`${AUTH_BASE}${path}`, {
    method: "POST",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload ?? {}),
  });
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [attempt, setAttempt] = useState(0);
  const generation = useRef(0);

  const applyUser = useCallback((next: AdminUser | null, nextStatus: AuthStatus) => {
    setUser(next);
    setStatus(nextStatus);
  }, []);

  const retry = useCallback(() => {
    setStatus("loading");
    setAttempt((current) => current + 1);
  }, []);

  useEffect(() => {
    const requestAttempt = attempt;
    const gen = generation.current;
    let cancelled = false;

    const load = async () => {
      // Reading `attempt` keeps this effect tied to Retry. Both values are from
      // the same render; a newer retry starts a new effect.
      if (requestAttempt !== attempt) {
        return;
      }
      try {
        const response = await fetch(`${AUTH_BASE}/status`, {
          method: "GET",
          credentials: "include",
          headers: { Accept: "application/json" },
        });
        if (cancelled || gen !== generation.current) {
          return;
        }
        if (response.status === 401) {
          applyUser(null, "unauthenticated");
          return;
        }
        const body = await readBody(response);
        if (cancelled || gen !== generation.current) {
          return;
        }
        if (response.ok && isUserPayload(body.user)) {
          applyUser(toUser(body.user), "authenticated");
          return;
        }
        applyUser(null, "error");
      } catch {
        if (cancelled || gen !== generation.current) {
          return;
        }
        applyUser(null, "error");
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [attempt, applyUser]);

  useEffect(() => {
    return subscribeSessionExpired(() => {
      // Ignore a status response that started before the cookie died.
      generation.current += 1;
      applyUser(null, "unauthenticated");
    });
  }, [applyUser]);

  const requestMagicLink = useCallback(async (email: string): Promise<AuthFailure | { kind: "sent" }> => {
    try {
      const response = await postJson("/magic-link", { email });
      const body = await readBody(response);
      if (response.ok && body.ok === true) {
        return { kind: "sent" };
      }
      return failureFromStatus(response.status, messageFrom(body, "Request failed"));
    } catch {
      return { kind: "error", message: "Request failed" };
    }
  }, []);

  const consumeMagicLink = useCallback(
    async (token: string): Promise<LoginResult> => {
      const existing = consumeCache.get(token);
      if (existing) {
        return existing;
      }

      const promise = (async (): Promise<LoginResult> => {
        generation.current += 1;
        const gen = generation.current;
        try {
          const response = await postJson("/magic-link/consume", { token });
          const body = await readBody(response);
          if (gen !== generation.current) {
            return { kind: "error", message: "Request failed" };
          }
          if (response.ok && body.requires_second_factor === true) {
            applyUser(null, "unauthenticated");
            return { kind: "second_factor" };
          }
          if (response.ok && isUserPayload(body.user)) {
            const next = toUser(body.user);
            applyUser(next, "authenticated");
            return { kind: "authenticated", user: next };
          }
          return failureFromStatus(response.status, messageFrom(body, "Invalid or expired link."));
        } catch {
          consumeCache.delete(token);
          return { kind: "error", message: "Request failed" };
        }
      })();

      consumeCache.set(token, promise);
      return promise;
    },
    [applyUser],
  );

  const submitSecondFactor = useCallback(
    async (code: string): Promise<SecondFactorResult> => {
      generation.current += 1;
      const gen = generation.current;
      try {
        const response = await postJson("/2fa", { code });
        const body = await readBody(response);
        if (gen !== generation.current) {
          return { kind: "error", message: "Request failed" };
        }
        if (response.ok && isUserPayload(body.user)) {
          const next = toUser(body.user);
          applyUser(next, "authenticated");
          return { kind: "authenticated", user: next };
        }
        if (response.status === 401) {
          return {
            kind: "invalid_code",
            message: messageFrom(body, "Invalid code."),
          };
        }
        return failureFromStatus(response.status, messageFrom(body, "Request failed"));
      } catch {
        return { kind: "error", message: "Request failed" };
      }
    },
    [applyUser],
  );

  const changePassword = useCallback(
    async (currentPassword: string, newPassword: string): Promise<PasswordResult> => {
      generation.current += 1;
      const gen = generation.current;
      try {
        const response = await postJson("/password", {
          current_password: currentPassword,
          new_password: newPassword,
        });
        const body = await readBody(response);
        if (gen !== generation.current) {
          return { kind: "error", message: "Request failed" };
        }
        if (response.ok && isUserPayload(body.user)) {
          const next = toUser(body.user);
          applyUser(next, "authenticated");
          return { kind: "authenticated", user: next };
        }
        // 401 is no usable session (a wrong current password is 422). /password
        // is outside AuthGate, so drop the client session and leave for /login.
        if (response.status === 401) {
          markSessionExpired();
          assignAdminPath("/login");
          return { kind: "error", message: messageFrom(body, "Request failed") };
        }
        return failureFromStatus(response.status, messageFrom(body, "Request failed"));
      } catch {
        return { kind: "error", message: "Request failed" };
      }
    },
    [applyUser],
  );

  const signOut = useCallback(async () => {
    generation.current += 1;
    try {
      await postJson("/logout");
    } catch {
      // Still leave the page. A failed logout must not keep the signed-in view up.
    }
    applyUser(null, "unauthenticated");
    assignAdminPath("/login");
  }, [applyUser]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading: status === "loading",
      status,
      retry,
      requestMagicLink,
      consumeMagicLink,
      submitSecondFactor,
      changePassword,
      signOut,
    }),
    [user, status, retry, requestMagicLink, consumeMagicLink, submitSecondFactor, changePassword, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthGate = ({ children }: { children: ReactNode }) => {
  const { user, status, retry } = useAuth();

  useEffect(() => {
    if (status === "loading" || status === "error") {
      return;
    }
    if (status === "unauthenticated" || !user) {
      assignAdminPath("/login");
      return;
    }
    if (user.requiresPasswordChange) {
      assignAdminPath("/password");
    }
  }, [status, user]);

  if (status === "loading") {
    return null;
  }

  if (status === "error") {
    return (
      <div className="flex min-h-svh flex-1 flex-col items-center justify-center gap-4 p-6">
        <div className="w-full max-w-md space-y-4 text-center">
          <h1 className="text-lg font-semibold tracking-tight">We could not load your account</h1>
          <p className="text-sm text-muted-foreground">Refresh the page and try again.</p>
          <Button type="button" variant="secondary" onClick={retry}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (status !== "authenticated" || !user || user.requiresPasswordChange) {
    return null;
  }

  return children;
};
